import { motion } from "framer-motion";
import { Check, Download, Mail, Paperclip, Send, CheckCircle2, Search } from "lucide-react";
import { MockShell, Spot } from "../Frames";

const ROWS = [
  ["11/06", "Jack Murray", "Lot 12 Banksia Cres", "06:48", "15:20", "0:30", "8.0"],
  ["11/06", "Liam O'Brien", "27 Wattle St", "07:00", "15:30", "0:30", "8.0"],
  ["11/06", "Mia Russo", "Unit 4/9 Ryrie St", "07:15", "13:45", "0:30", "6.0"],
  ["12/06", "Jack Murray", "Lot 12 Banksia Cres", "06:45", "14:15", "0:30", "7.0"],
  ["12/06", "Sam Nguyen", "Lot 88 Federation Dr", "07:30", "16:00", "0:30", "8.0"],
  ["12/06", "Liam O'Brien", "27 Wattle St", "06:55", "15:25", "0:30", "8.0"],
];
const Pick = ({ children }) => <span className="h-7 rounded-md border border-slate-200 bg-white px-2.5 flex items-center gap-1.5 text-[11px]">{children}</span>;

const Timesheets = () => (
  <MockShell tab="Timesheets">
    <Spot on className="mb-3">
      <div className="flex gap-2 bg-white rounded-lg border border-slate-200 p-2">
        <Pick>Worker: All crew</Pick><Pick>From 11/06/2026</Pick><Pick>To 17/06/2026</Pick>
        <span className="flex-1"><Pick><Search className="w-3 h-3 text-slate-400" /><span className="text-slate-400">Search job…</span></Pick></span>
      </div>
    </Spot>
    <div className="rounded-lg bg-white border border-slate-200 overflow-hidden">
      <div className="grid grid-cols-[70px_100px_1fr_50px_50px_50px_50px] px-3 h-8 items-center text-[10px] font-semibold text-slate-500 bg-slate-50">
        {["Date", "Worker", "Job", "On", "Off", "Break", "Hours"].map((h) => <span key={h}>{h}</span>)}
      </div>
      {ROWS.map((r, i) => (
        <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.08 * i }}
          className="grid grid-cols-[70px_100px_1fr_50px_50px_50px_50px] px-3 h-9 items-center text-[11px] border-t border-slate-100">
          {r.map((c, j) => <span key={j} className={j >= 3 ? "font-mono" : j === 1 ? "font-semibold" : "truncate pr-2"}>{c}</span>)}
        </motion.div>
      ))}
    </div>
  </MockShell>
);

const WEEK = [{ name: "Jack Murray", days: [8, 7, 0, 0, 8.5, 8, 7.5], ok: true }, { name: "Liam O'Brien", days: [8, 8, 0, 0, 8, 8, 8], on: true }, { name: "Mia Russo", days: [6, 0, 0, 0, 7, 7.5, 0] }];
const Approvals = () => (
  <MockShell tab="Timesheets">
    <p className="font-display font-bold text-[16px] mb-3">Pay period approvals <span className="font-mono text-[11px] text-slate-500 font-semibold ml-2">11 – 17 Jun</span></p>
    <div className="space-y-2.5">
      {WEEK.map((w) => (
        <div key={w.name} className="rounded-lg bg-white border border-slate-200 px-4 py-3 flex items-center gap-4">
          <p className="w-28 font-semibold">{w.name}</p>
          <div className="flex items-end gap-1.5 h-10 flex-1">
            {w.days.map((h, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                <motion.div initial={{ height: 0 }} animate={{ height: `${(h / 9) * 28}px` }} transition={{ delay: 0.05 * i }} className="w-full max-w-[22px] rounded-sm bg-[#1E3A5F]" />
                <span className="text-[8px] font-mono text-slate-400">{11 + i}</span>
              </div>
            ))}
          </div>
          <p className="font-mono font-bold text-[15px] w-14 text-right">{w.days.reduce((a, b) => a + b, 0)}h</p>
          <Spot on={w.on}>
            {w.ok
              ? <span className="h-8 w-24 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold flex items-center justify-center gap-1"><Check className="w-3 h-3" /> Approved</span>
              : <span className="h-8 w-24 rounded-full bg-[#1E3A5F] text-white text-[11px] font-semibold grid place-items-center">Approve</span>}
          </Spot>
        </div>
      ))}
    </div>
  </MockShell>
);

const PAY = [["Jack Murray", "Full-time", 5, "39.0"], ["Liam O'Brien", "Full-time", 5, "40.0"], ["Mia Russo", "Casual", 3, "20.5"], ["Sam Nguyen", "Casual", 2, "16.0"]];
const Summary = () => (
  <MockShell tab="Timesheets">
    <div className="flex items-center mb-3">
      <p className="font-display font-bold text-[16px]">Payroll summary</p>
      <span className="ml-2 font-mono text-[11px] text-slate-500 font-semibold">11 – 17 Jun 2026</span>
      <span className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-[11px] font-semibold"><Download className="w-3 h-3" /> Download PDF</span>
    </div>
    <div className="rounded-lg bg-white border border-slate-200 overflow-hidden">
      <div className="grid grid-cols-[1fr_110px_70px_90px] px-4 h-8 items-center text-[10px] font-semibold text-slate-500 bg-slate-50">
        <span>Worker</span><span>Type</span><span>Days</span><span className="text-right">Hours</span>
      </div>
      {PAY.map(([n, t, d, h]) => (
        <div key={n} className="grid grid-cols-[1fr_110px_70px_90px] px-4 h-10 items-center text-[12px] border-t border-slate-100">
          <span className="font-semibold">{n}</span><span className="text-slate-500">{t}</span><span className="font-mono">{d}</span><span className="font-mono text-right">{h}</span>
        </div>
      ))}
      <Spot on>
        <div className="grid grid-cols-[1fr_110px_70px_90px] px-4 h-11 items-center text-[13px] bg-[#1E3A5F] text-white font-bold">
          <span>Total</span><span /><span className="font-mono">15</span><span className="font-mono text-right">115.5h</span>
        </div>
      </Spot>
    </div>
  </MockShell>
);

const Emailed = () => (
  <MockShell tab="Timesheets">
    <div className="flex gap-5 h-full">
      <motion.div initial={{ rotate: -4, y: 12, opacity: 0 }} animate={{ rotate: -2, y: 0, opacity: 1 }} className="w-[270px] bg-white shadow-xl border border-slate-200 rounded-sm overflow-hidden">
        <div className="bg-[#1E3A5F] text-white px-4 py-3">
          <p className="font-display font-black text-[12px] uppercase tracking-tight">Southern Cross Plastering</p>
          <p className="text-[9px] text-white/70">Payroll summary · 11 – 17 Jun 2026</p>
        </div>
        <div className="px-4 py-2 space-y-1.5">
          {PAY.map(([n, , , h]) => <div key={n} className="flex justify-between text-[10px] border-b border-slate-100 pb-1"><span>{n}</span><span className="font-mono">{h}h</span></div>)}
          <div className="flex justify-between text-[11px] font-bold pt-1"><span>Total</span><span className="font-mono">115.5h</span></div>
        </div>
        <div className="h-1.5 bg-[#EA580C] mt-6" />
      </motion.div>
      <div className="flex-1 rounded-xl bg-white border border-slate-200 p-4 space-y-2.5 self-start">
        <p className="font-display font-bold text-[14px] flex items-center gap-2"><Mail className="w-4 h-4 text-[#EA580C]" /> Email bookkeeper</p>
        <div className="h-8 rounded-md border border-slate-200 px-3 flex items-center text-[11px]"><span className="text-slate-400 mr-2">To</span>accounts@southerncross.com.au</div>
        <div className="h-8 rounded-md border border-slate-200 px-3 flex items-center text-[11px]"><span className="text-slate-400 mr-2">Subject</span>Payroll · 11 – 17 Jun</div>
        <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-1 text-[10px] font-mono"><Paperclip className="w-3 h-3" />payroll-2026-06-11.pdf</span>
        <Spot on><div className="h-9 rounded-full bg-[#EA580C] text-white text-[12px] font-semibold flex items-center justify-center gap-1.5"><Send className="w-3.5 h-3.5" /> Send PDF</div></Spot>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }} className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700">
          <CheckCircle2 className="w-4 h-4" /> Sent to your bookkeeper
        </motion.p>
      </div>
    </div>
  </MockShell>
);

const STEPS = [Timesheets, Approvals, Summary, Emailed];
export const PayrollScreens = ({ step }) => {
  const S = STEPS[step];
  return <S />;
};
